"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    MILLESIME,
    champsDuFormulaire,
    choixDAnnee,
    concerne,
    departementProbable,
    recapitulatif,
    valider,
} from "@/lib/tools/cfeInitialReturn";
import { telechargerFormulaire } from "@/lib/tools/officialFormPdf";
import { dateEnToutesLettres, nomDuFichier } from "@/lib/tools/documentText";
import {
    Avancer,
    Champ,
    Choix,
    Ecran,
    Recommencer,
    Resultat,
    Segments,
    remplir,
    useParcours,
} from "./Journey";
import { useDossierOutil, useSignalerIdentite } from "./ToolsDossier";

/**
 * La déclaration initiale de CFE, formulaire 1447-C-SD.
 *
 * LE SEUL OUTIL QUI NE COMPOSE PAS SON DOCUMENT. Les trois autres formulaires
 * écrivent une lettre ; celui-ci remplit un imprimé officiel, téléchargé vierge
 * puis complété case par case dans le navigateur. Le lecteur ne peut donc pas
 * relire le PDF dans la page : il relit un récapitulatif, qui dit en français
 * ce qui partira dans les cases, y compris ce que le parcours coche sans le
 * demander.
 *
 * DEUX TRIS EN TÊTE, et ils écartent beaucoup de monde. L'imprimé ne vaut que
 * pour son millésime : qui a créé avant relève du 1447-M-SD, qui a créé après
 * attend l'imprimé de son année, et qui n'a pas encore créé n'a rien à déposer.
 * Et qui dispose d'un local dédié doit remplir le cadre C, que cet outil ne
 * couvre pas : mieux vaut l'envoyer à l'imprimé complet que lui rendre une
 * déclaration à moitié faite.
 */

const L = {
    fr: {
        phases: ["Votre situation", "Votre entreprise", "Signature"],
        retour: "Retour",
        prive: "Tout se fait sur votre appareil : l'imprimé officiel s'y remplit, personne ne reçoit vos réponses, pas même nous, et tout disparaît avec l'onglet.",
        quandTitre: "Quand avez-vous créé votre entreprise ?",
        quandAide: "L'imprimé est millésimé : il ne déclare que les établissements créés en {annee}. Si ce n'est pas votre cas, vous saurez quoi faire à la place.",
        quandCette: "En {annee}",
        quandCetteDetail: "L'année que cet imprimé couvre",
        quandEnCours: "En {annee}",
        quandEnCoursDetail: "L'imprimé de cette année n'est pas encore publié",
        quandAvant: "Avant {annee}",
        quandAvantDetail: "Votre déclaration initiale est passée",
        quandPasEncore: "Pas encore créée",
        quandPasEncoreDetail: "L'immatriculation est à venir",
        localTitre: "Où exercez-vous votre activité ?",
        localAide: "La réponse décide de la moitié du formulaire : sans local dédié, la page 3 ne vous concerne pas.",
        localDomicile: "Chez moi ou chez mes clients",
        localDomicileDetail: "Aucun local dédié à l'activité",
        localDedie: "Dans un local dédié",
        localDedieDetail: "Bureau, atelier, boutique",
        horsSujetTitre: "Ce n'est pas le bon formulaire",
        motifAvant: "Votre déclaration initiale est derrière vous : elle se dépose l'année de la création, et cet imprimé est millésimé {annee}. C'est le 1447-M-SD qui prend le relais, en cas de changement seulement, avant le deuxième jour ouvré suivant le 1er mai.",
        attenteTitre: "L'imprimé {annee} n'est pas encore sorti",
        attenteTexte: "L'administration publie un imprimé par an, au début de l'automne. Celui de {annee} n'est pas encore en ligne, et vous avez jusqu'au 31 décembre pour le déposer : rien ne presse aujourd'hui.",
        attenteAppel: "Laissez votre adresse. Nous surveillons sa parution chaque semaine, et nous vous écrivons le jour où il sort.",
        attenteChamp: "Votre adresse e-mail",
        attenteBouton: "Me prévenir à sa parution",
        attenteEnvoi: "Enregistrement…",
        attenteMerci: "C'est noté. Vous recevrez un message le jour où l'imprimé {annee} est en ligne.",
        attenteDeja: "Votre adresse est déjà sur la liste. Vous serez prévenu à la parution.",
        attenteErreur: "L'inscription n'a pas pu être enregistrée. Réessayez dans un instant.",
        attentePrive: "Cette adresse ne sert qu'à ce message, et à rien d'autre.",
        motifPasEncore: "Rien à déposer tant que l'entreprise n'existe pas. Revenez une fois votre SIRET reçu : vous aurez jusqu'au 31 décembre de l'année de création, et cet imprimé ne se remplit qu'avec le numéro SIRET en main.",
        motifLocalDedie: "Avec un local dédié, vous devez décrire vos locaux au cadre C de la page 3 : surfaces, nature, valeur locative. Cet outil ne couvre pas ce cadre, et une déclaration à moitié remplie vous vaudrait une demande de régularisation. Téléchargez l'imprimé vierge sur impots.gouv et remplissez-le en entier.",
        entrepriseTitre: "Votre entreprise",
        entrepriseAide: "Reprenez les mentions de votre avis de situation SIRENE ou de votre notification d'immatriculation.",
        champPrenoms: "Vos prénoms",
        champNom: "Votre nom",
        champSiret: "Numéro SIRET",
        aideSiret: "Quatorze chiffres, espaces autorisés. Celui de l'établissement, pas le SIREN à neuf chiffres.",
        champCodeApe: "Code APE",
        aideCodeApe: "Facultatif. Quatre chiffres et une lettre, par exemple 7410Z.",
        champActivites: "Activités exercées",
        aideActivites: "En clair, comme vous les décririez à un client. Par exemple : graphisme et identité visuelle.",
        champArtisan: "Êtes-vous inscrit au registre national des entreprises comme artisan ?",
        aideArtisan: "Oui si votre activité est artisanale : bâtiment, fabrication, réparation, coiffure, esthétique... Votre notification d'immatriculation le précise. Le formulaire exige une réponse, nous n'en supposons aucune.",
        oui: "Oui",
        non: "Non",
        adresseTitre: "L'adresse de votre établissement",
        adresseAide: "Celle que vous avez déclarée au guichet unique. C'est elle qui détermine la commune d'imposition.",
        champVoie: "Numéro et voie",
        champCodePostal: "Code postal",
        champCommune: "Commune",
        champDepartement: "Département",
        aideDepartement: "Déduit de votre code postal. Corrigez-le si besoin, la Corse et l'outre-mer ne se devinent pas toujours.",
        activiteTitre: "Votre activité en {annee}",
        activiteAide: "Le chiffre d'affaires demandé est une estimation pour l'année entière, pas un montant déjà encaissé.",
        champDateCreation: "Date de création de l'entreprise",
        champChiffreDAffaires: "Chiffre d'affaires estimé pour {annee}",
        aideChiffreDAffaires: "En euros, hors taxes, sans centimes. Une estimation sincère suffit.",
        champSurface: "Surface occupée à domicile",
        aideSurface: "En mètres carrés, au moins 1. La pièce ou le coin de pièce où vous travaillez : un bureau dans un salon vaut 1 m².",
        microSocial: "Je bénéficie du régime micro-social",
        microSocialAide: "C'est le cas de la quasi-totalité des micro-entrepreneurs. Le formulaire demande alors la date d'entrée dans le statut.",
        champDateMicroSocial: "Date d'entrée dans le statut",
        tempsPartiel: "J'exerce à temps partiel ou moins de neuf mois par an",
        signatureTitre: "Où et quand signez-vous ?",
        signatureAide: "L'imprimé se signe à la main après impression, ou s'envoie depuis la messagerie sécurisée de votre espace professionnel.",
        champLieuSignature: "Lieu de signature",
        champDateSignature: "Date de signature",
        continuer: "Continuer",
        voirDocument: "Voir ce qui sera déclaré",
        resultatTitre: "Votre déclaration",
        recapTitre: "Ce qui partira dans l'imprimé",
        recapAide: "Relisez avant de télécharger : le formulaire officiel s'ouvrira rempli de ces valeurs, et vous pourrez encore le corriger dans votre lecteur de PDF.",
        telecharger: "Télécharger l'imprimé rempli",
        recommencer: "Recommencer",
        manquesTitre: "Il manque encore quelque chose",
        manquesAide: "Complétez les champs signalés pour obtenir votre imprimé.",
        verifierTitre: "Une valeur à vérifier",
        verifierAide: "Ces champs sont remplis, mais leur contenu ne passe pas. Corrigez-les et revenez ici.",
        erreurDateLue: "Nous lisons {date}. Vérifiez l'année.",
        erreurFormat: "Ce format n'est pas reconnu.",
        erreurSiret: "Un SIRET compte quatorze chiffres. Le vôtre en a {compte}.",
        erreurApe: "Un code APE s'écrit avec quatre chiffres et une lettre, par exemple 7410Z.",
        erreurHorsMillesime: "Cet imprimé ne déclare que les créations de {annee}.",
        alerteHorsDelai: "La date limite de dépôt était le 31 décembre {annee}. Déposez quand même : une déclaration tardive vaut mieux qu'une absence de déclaration, et le service des impôts la traitera.",
        suiteTitre: "Et maintenant",
        suite1: "Imprimez l'imprimé, signez-le, et adressez-le à votre service des impôts des entreprises avant le 31 décembre {annee}.",
        suite2: "Vous pouvez aussi le déposer sans l'imprimer, depuis la messagerie sécurisée de votre espace professionnel sur impots.gouv.fr.",
        suite3: "Gardez-en une copie : c'est cette déclaration qui fixera votre base de CFE pour les années suivantes.",
        fondTitre: "Ce que cette déclaration décide",
        fond: "Travailler de chez vous n'en dispense pas : dès que la valeur locative de ce que vous venez de déclarer reste sous la base minimum de votre commune, c'est cette base minimum qui s'y substitue. Seul un chiffre d'affaires de référence inférieur ou égal à 5 000 € en dispense. Ce que vous paierez au titre de {cfeDe} dépendra ensuite du taux voté par votre commune, et l'avis ne viendra pas par courrier : il se consulte dans votre espace professionnel sur impots.gouv.fr, à régler au plus tard le 15 décembre.",
        millesimeNote: "Imprimé {cerfa}, millésime {annee}, tel que publié par la direction générale des finances publiques.",
    },
    en: {
        phases: ["Your situation", "Your business", "Signature"],
        retour: "Back",
        prive: "Everything happens on your device: the official form is filled in there, nobody receives your answers, not even us, and it all disappears with the tab.",
        quandTitre: "When did you register your business?",
        quandAide: "The form carries a year: it only declares establishments created in {annee}. If that is not your case, you will find out what to do instead.",
        quandCette: "In {annee}",
        quandCetteDetail: "The year this form covers",
        quandAvant: "Another year",
        quandEnCours: "In {annee}",
        quandEnCoursDetail: "This year's form is not published yet",
        quandAvant: "Before {annee}",
        quandAvantDetail: "Your initial return is behind you",
        quandPasEncore: "Not yet registered",
        quandPasEncoreDetail: "Registration is still to come",
        localTitre: "Where do you carry out your activity?",
        localAide: "The answer settles half the form: with no dedicated premises, page 3 does not concern you.",
        localDomicile: "At home or at my clients'",
        localDomicileDetail: "No premises dedicated to the activity",
        localDedie: "In dedicated premises",
        localDedieDetail: "An office, a workshop, a shop",
        horsSujetTitre: "This is not the right form",
        motifAvant: "Your initial return is behind you: it is filed in the year of creation, and this form carries the year {annee}. Form 1447-M-SD takes over, only if something changed, by the second working day after 1 May.",
        attenteTitre: "The {annee} form is not out yet",
        attenteTexte: "The authority publishes one form a year, in early autumn. The {annee} one is not online yet, and you have until 31 December to file it: there is no rush today.",
        attenteAppel: "Leave your email address. We watch for its release every week, and we write to you the day it comes out.",
        attenteChamp: "Your email address",
        attenteBouton: "Tell me when it is out",
        attenteEnvoi: "Saving…",
        attenteMerci: "Noted. You will get a message the day the {annee} form goes online.",
        attenteDeja: "Your address is already on the list. You will be told when it is released.",
        attenteErreur: "We could not save your address. Please try again in a moment.",
        attentePrive: "This address is used for that message only, and nothing else.",
        motifPasEncore: "Nothing to file until the business exists. Come back once you have your SIRET number: you will have until 31 December of the year of creation, and this form cannot be filled in without that number.",
        motifLocalDedie: "With dedicated premises, you must describe them in box C on page 3: floor areas, nature, rental value. This tool does not cover that box, and a half-filled return would earn you a request for correction. Download the blank form from impots.gouv and fill it in completely.",
        entrepriseTitre: "Your business",
        entrepriseAide: "Use the wording from your SIRENE situation notice or your registration confirmation.",
        champPrenoms: "Your forenames",
        champNom: "Your surname",
        champSiret: "SIRET number",
        aideSiret: "Fourteen digits, spaces allowed. The establishment's number, not the nine-digit SIREN.",
        champCodeApe: "APE code",
        aideCodeApe: "Optional. Four digits and a letter, for example 7410Z.",
        champActivites: "Activities carried out",
        aideActivites: "In plain words, as you would describe them to a client. For example: graphic design and visual identity.",
        champArtisan: "Are you registered in the national business register as a craftsperson?",
        aideArtisan: "Yes if your activity is a craft: building trades, making, repairing, hairdressing, beauty care... Your registration notice says so. The form requires an answer, and we assume none.",
        oui: "Yes",
        non: "No",
        adresseTitre: "Your establishment's address",
        adresseAide: "The one you declared to the single window. It determines the taxing municipality.",
        champVoie: "Number and street",
        champCodePostal: "Postcode",
        champCommune: "Town",
        champDepartement: "Department",
        aideDepartement: "Taken from your postcode. Correct it if needed, Corsica and the overseas departments cannot always be guessed.",
        activiteTitre: "Your activity in {annee}",
        activiteAide: "The turnover asked for is an estimate for the whole year, not an amount already received.",
        champDateCreation: "Date the business was created",
        champChiffreDAffaires: "Estimated turnover for {annee}",
        aideChiffreDAffaires: "In euros, excluding tax, no cents. An honest estimate is enough.",
        champSurface: "Floor area used at home",
        aideSurface: "In square metres, at least 1. The room, or corner of a room, where you work: a desk in a living room counts as 1 m².",
        microSocial: "I am under the micro-social scheme",
        microSocialAide: "That is the case for almost every micro-entrepreneur. The form then asks for the date you entered the scheme.",
        champDateMicroSocial: "Date you entered the scheme",
        tempsPartiel: "I work part time, or fewer than nine months a year",
        signatureTitre: "Where and when are you signing?",
        signatureAide: "The form is signed by hand after printing, or sent from the secure messaging of your professional account.",
        champLieuSignature: "Place of signature",
        champDateSignature: "Date of signature",
        continuer: "Continue",
        voirDocument: "See what will be declared",
        resultatTitre: "Your return",
        recapTitre: "What will go into the form",
        recapAide: "Read it before downloading: the official form will open filled with these values, and you can still correct it in your PDF reader.",
        telecharger: "Download the filled form",
        recommencer: "Start again",
        manquesTitre: "Something is still missing",
        manquesAide: "Fill in the highlighted fields to get your form.",
        verifierTitre: "Something to check",
        verifierAide: "These fields are filled in, but their content is not accepted. Correct them and come back.",
        erreurDateLue: "We read {date}. Please check the year.",
        erreurFormat: "This format is not recognised.",
        erreurSiret: "A SIRET has fourteen digits. Yours has {compte}.",
        erreurApe: "An APE code is four digits and a letter, for example 7410Z.",
        erreurHorsMillesime: "This form only declares businesses created in {annee}.",
        alerteHorsDelai: "The filing deadline was 31 December {annee}. File it anyway: a late return is better than no return, and the tax office will process it.",
        suiteTitre: "What happens next",
        suite1: "Print the form, sign it, and send it to your business tax office before 31 December {annee}.",
        suite2: "You can also file it without printing, from the secure messaging of your professional account on impots.gouv.fr.",
        suite3: "Keep a copy: this return sets your business property tax base for the following years.",
        fondTitre: "What this return decides",
        fond: "Working from home does not exempt you: as soon as the rental value of what you have just declared stays below your municipality's minimum base, that minimum base takes its place. Only a reference turnover of 5,000 € or less removes it. What you pay for {cfeDe} then depends on the rate voted by your municipality, and the notice will not arrive by post: you read it in your professional account on impots.gouv.fr, and it is due by 15 December at the latest.",
        millesimeNote: "Form {cerfa}, {annee} edition, as published by the French public finances directorate.",
    },
};

const PHASES_PAR_ECRAN = {
    quand: 0,
    local: 0,
    horsSujet: 0,
    entreprise: 1,
    adresse: 1,
    activite: 1,
    signature: 2,
    resultat: 2,
};

const VIDE = {
    anneeCreation: "",
    local: "",
    prenoms: "",
    nom: "",
    siret: "",
    codeApe: "",
    activites: "",
    artisan: "",
    voie: "",
    codePostal: "",
    commune: "",
    departement: "",
    departementTouche: false,
    dateCreation: "",
    chiffreDAffaires: "",
    surface: "",
    microSocial: true,
    dateMicroSocial: "",
    tempsPartiel: false,
    lieuDeSignature: "",
    dateDeSignature: "",
};

/** Une case à cocher, pour les trois options du cadre B2. */
function Case({ libelle, aide, coche, basculer }) {
    return (
        <div>
            <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                    type="checkbox"
                    checked={coche}
                    onChange={(e) => basculer(e.target.checked)}
                    className="h-4 w-4 accent-primary cursor-pointer"
                />
                <span className="text-sm text-gray-600 dark:text-lightBlue">{libelle}</span>
            </label>
            {aide && (
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1.5 mb-0 pl-7">
                    {aide}
                </p>
            )}
        </div>
    );
}

/** Les libellés des champs, hors du dictionnaire lu par le contrôle de parité. */
const LIBELLES_MANQUES = {
    fr: {
        prenoms: "Vos prénoms",
        nom: "Votre nom",
        siret: "Votre numéro SIRET",
        codeApe: "Votre code APE",
        activites: "Vos activités exercées",
        artisan: "Votre inscription comme artisan, oui ou non",
        voie: "L'adresse de l'établissement",
        codePostal: "Le code postal",
        commune: "La commune",
        departement: "Le département",
        dateCreation: "La date de création",
        chiffreDAffaires: "Le chiffre d'affaires estimé",
        surface: "La surface occupée à domicile",
        dateMicroSocial: "La date d'entrée dans le régime micro-social",
        lieuDeSignature: "Le lieu de signature",
        dateDeSignature: "La date de signature",
        anneeCreation: "Votre année de création",
    },
    en: {
        prenoms: "Your forenames",
        nom: "Your surname",
        siret: "Your SIRET number",
        codeApe: "Your APE code",
        activites: "Your activities",
        artisan: "Your craftsperson registration, yes or no",
        voie: "The establishment's address",
        codePostal: "The postcode",
        commune: "The town",
        departement: "The department",
        dateCreation: "The date of creation",
        chiffreDAffaires: "The estimated turnover",
        surface: "The floor area used at home",
        dateMicroSocial: "The date you entered the micro-social scheme",
        lieuDeSignature: "The place of signature",
        dateDeSignature: "The date of signature",
        anneeCreation: "Your year of creation",
    },
};

export default function DeclarationInitialeCfe({ lang = "fr", aujourdhui }) {
    const t = L[lang] || L.fr;
    const { identite } = useDossierOutil();
    /* L'identité gardée sur l'appareil préremplit les écrans « entreprise » et
       « adresse », que le lecteur relit : rien n'est sauté. L'imprimé n'a pas
       de ligne de complément d'adresse : le complément rejoint la voie
       (lib/tools/dossier.js). Le département suit le code postal repris
       comme s'il avait été tapé. */
    const departIdentite = () => {
        const depart = { ...VIDE };
        for (const cle of ["prenoms", "nom", "siret", "codeApe", "voie", "codePostal", "commune"]) {
            if (identite[cle]) depart[cle] = identite[cle];
        }
        if (depart.codePostal) depart.departement = departementProbable(depart.codePostal);
        return depart;
    };
    const [r, setR] = useState(departIdentite);
    const [erreurTelechargement, setErreurTelechargement] = useState(false);
    /* La liste d'attente de l'imprimé de l'année en cours. « etat » vaut
       "repos", "envoi", "merci", "deja" ou "erreur" : le parcours ne montre
       jamais un bouton qui ne fait rien, et ne redemande pas une adresse déjà
       enregistrée. */
    const [adresse, setAdresse] = useState("");
    const [attente, setAttente] = useState("repos");

    const { etape, sens, reduit, rang, aller, retour, recommencer: rejouer, communs } = useParcours(
        "quand",
        () => {
            // L'identité gardée revient : on recommence le document, pas sa saisie.
            setR(departIdentite());
            setErreurTelechargement(false);
        },
        PHASES_PAR_ECRAN,
        null,
        // Reprendre où l'on en était, le temps de l'onglet (Journey.jsx).
        { r, setR },
    );

    const poser = (cle) => (valeur) => setR((p) => ({ ...p, [cle]: valeur }));

    // L'identité se propose à la garde une fois l'adresse passée.
    useSignalerIdentite(
        {
            prenoms: r.prenoms,
            nom: r.nom,
            siret: r.siret,
            codeApe: r.codeApe,
            voie: r.voie,
            codePostal: r.codePostal,
            commune: r.commune,
        },
        etape === "activite" || etape === "signature" || etape === "resultat",
    );

    /* Le département suit le code postal tant que personne ne l'a corrigé : le
       formulaire le demande à part, et le redemander serait une question de
       plus pour une réponse que le code postal donne presque toujours. */
    const poserCodePostal = (valeur) =>
        setR((p) => ({
            ...p,
            codePostal: valeur,
            departement: p.departementTouche ? p.departement : departementProbable(valeur),
        }));
    const poserDepartement = (valeur) =>
        setR((p) => ({ ...p, departement: valeur, departementTouche: true }));

    const jour = String(aujourdhui).slice(0, 10);
    const reponses = useMemo(
        () => ({ ...r, dateDeSignature: r.dateDeSignature || jour }),
        [r, jour],
    );

    const controle = useMemo(() => valider(reponses, aujourdhui), [reponses, aujourdhui]);
    const champs = useMemo(() => champsDuFormulaire(reponses), [reponses]);
    const recap = useMemo(() => recapitulatif(reponses), [reponses]);
    const rempli = (...cles) => cles.every((c) => String(r[c] || "").trim());

    const refusDe = (champ) => (controle.invalides || []).find((i) => i.champ === champ);
    const erreurDe = (champ) => {
        const refus = refusDe(champ);
        if (!refus) return "";
        if (refus.motif === "siret") {
            return remplir(t.erreurSiret, {
                compte: String(r[champ] || "").replace(/\D/g, "").length,
            });
        }
        if (refus.motif === "ape") return t.erreurApe;
        if (refus.motif === "horsMillesime") {
            return remplir(t.erreurHorsMillesime, { annee: MILLESIME.creationsDe });
        }
        if (refus.motif === "ageImpossible") {
            return remplir(t.erreurDateLue, { date: dateEnToutesLettres(r[champ]) });
        }
        return t.erreurFormat;
    };

    const commun = { ...communs, phases: t.phases, retour: t.retour, rang };
    const motif = concerne(r).motif;
    const annee = { annee: MILLESIME.creationsDe };

    /* La première question compte trois ou quatre cases selon que l'imprimé de
       l'année en cours est publié ou non. Elles sont calculées, jamais écrites
       en dur : le jour où MILLESIME avance, la case d'attente disparaît seule. */
    const LIBELLES = {
        anneeEnCours: { icone: "clock-circle-linear", titre: "quandEnCours", detail: "quandEnCoursDetail" },
        cetteAnnee: { icone: "calendar-linear", titre: "quandCette", detail: "quandCetteDetail" },
        avant: { icone: "calendar-mark-linear", titre: "quandAvant", detail: "quandAvantDetail" },
        pasEncore: { icone: "clipboard-check-linear", titre: "quandPasEncore", detail: "quandPasEncoreDetail" },
    };
    const cases = useMemo(
        () => choixDAnnee(jour).map((c) => {
            const l = LIBELLES[c.cle];
            const vals = { annee: c.annee ?? MILLESIME.creationsDe };
            return { cle: c.cle, icone: l.icone, titre: remplir(t[l.titre], vals), detail: remplir(t[l.detail], vals) };
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [jour, t],
    );
    /* Recommencer remet le parcours à zéro, mais l'état du champ d'attente vit à
       côté : sans ça, on retrouvait « c'est noté » sans avoir rien envoyé. */
    const recommencer = () => {
        setAdresse("");
        setAttente("repos");
        rejouer();
    };

    /* La page passe toujours la date du serveur : on ne la fabrique pas ici,
       le rendu doit rester le même à chaque passage. */
    const anneeAttendue = { annee: new Date(jour).getFullYear() };

    const telecharger = async () => {
        try {
            setErreurTelechargement(false);
            await telechargerFormulaire({
                champs,
                nomFichier: nomDuFichier(`1447-c-sd-${MILLESIME.annee}`, r),
            });
        } catch {
            // L'imprimé vierge n'a pas pu être chargé : le dire, plutôt que de
            // laisser un bouton qui ne fait rien.
            setErreurTelechargement(true);
        }
    };

    /* La liste d'attente passe par la route des listes d'attente, qui sait déjà
       dédoublonner et prévenir l'équipe. Le slug la range à part des formations,
       et « contexte » évite qu'un e-mail d'alerte parle d'une formation. */
    const rejoindreLaListe = async (event) => {
        event.preventDefault();
        if (attente === "envoi" || !adresse.trim()) return;
        setAttente("envoi");
        try {
            const reponse = await fetch("/api/course-waitlist/join", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    courseSlug: `outil-cfe-1447-c-${anneeAttendue.annee}`,
                    courseName: `Imprimé 1447-C millésime ${anneeAttendue.annee}`,
                    contexte: "outil",
                    email: adresse.trim(),
                }),
            });
            if (!reponse.ok) throw new Error("refus");
            const donnees = await reponse.json();
            setAttente(donnees.already ? "deja" : "merci");
        } catch {
            setAttente("erreur");
        }
    };

    /* ---------------------------------------------------------------- */
    /* Les écrans                                                        */
    /* ---------------------------------------------------------------- */

    if (etape === "quand") {
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={0}
                titre={t.quandTitre}
                aide={remplir(t.quandAide, annee)}
            >
                <div className={`grid ${cases.length > 3 ? "sm:grid-cols-2" : "sm:grid-cols-3"} gap-3`}>
                    {cases.map((c) => (
                        <Choix
                            key={c.cle}
                            icone={c.icone}
                            titre={c.titre}
                            detail={c.detail}
                            onClick={() => {
                                setR((p) => ({ ...p, anneeCreation: c.cle }));
                                aller(c.cle === "cetteAnnee" ? "local" : "horsSujet");
                            }}
                        />
                    ))}
                </div>
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.prive}</p>
            </Ecran>
        );
    }

    if (etape === "local") {
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={0}
                onRetour={retour}
                titre={t.localTitre}
                aide={t.localAide}
            >
                <div className="grid sm:grid-cols-2 gap-3">
                    {[
                        { cle: "domicile", icone: "home-smile-linear", titre: t.localDomicile, detail: t.localDomicileDetail },
                        { cle: "dedie", icone: "buildings-2-linear", titre: t.localDedie, detail: t.localDedieDetail },
                    ].map((c) => (
                        <Choix
                            key={c.cle}
                            icone={c.icone}
                            titre={c.titre}
                            detail={c.detail}
                            onClick={() => {
                                setR((p) => ({ ...p, local: c.cle }));
                                aller(c.cle === "domicile" ? "entreprise" : "horsSujet");
                            }}
                        />
                    ))}
                </div>
            </Ecran>
        );
    }

    /* Créé l'année en cours : ce n'est pas un refus, c'est une attente. L'imprimé
       existera dans quelques semaines, et cette personne a jusqu'au 31 décembre
       pour le déposer. On prend son adresse au lieu de la renvoyer. */
    if (etape === "horsSujet" && motif === "anneeEnCours") {
        return (
            <Resultat
                onRetour={retour}
                retour={t.retour}
                key={etape}
                titre={remplir(t.attenteTitre, anneeAttendue)}
                phases={t.phases}
                sens={sens}
                reduit={reduit}
            >
                <div className="mt-4">
                    <p className="text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                        {remplir(t.attenteTexte, anneeAttendue)}
                    </p>
                    {attente === "merci" || attente === "deja" ? (
                        <p className="text-[15px] leading-7 text-gray-800 dark:text-white font-medium mb-0">
                            {attente === "deja" ? t.attenteDeja : remplir(t.attenteMerci, anneeAttendue)}
                        </p>
                    ) : (
                        <>
                            <p className="text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                {t.attenteAppel}
                            </p>
                            <form onSubmit={rejoindreLaListe}>
                                <input
                                    type="email"
                                    required
                                    value={adresse}
                                    onChange={(e) => { setAdresse(e.target.value); if (attente === "erreur") setAttente("repos"); }}
                                    placeholder={t.attenteChamp}
                                    aria-label={t.attenteChamp}
                                    className="w-full max-w-md rounded-xl border border-gray-200 dark:border-white/15 bg-white dark:bg-white/5 px-4 py-3 text-[15px] text-gray-800 dark:text-white outline-hidden focus:border-gray-400 dark:focus:border-white/30"
                                />
                                <Avancer onClick={rejoindreLaListe} disabled={attente === "envoi" || !adresse.trim()}>
                                    {attente === "envoi" ? t.attenteEnvoi : t.attenteBouton}
                                </Avancer>
                            </form>
                            {attente === "erreur" && (
                                <p className="text-[13px] leading-6 text-red-600 dark:text-red-400 mt-3 mb-0">
                                    {t.attenteErreur}
                                </p>
                            )}
                            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-3 mb-0">{t.attentePrive}</p>
                        </>
                    )}
                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            </Resultat>
        );
    }

    if (etape === "horsSujet") {
        return (
            <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.horsSujetTitre} phases={t.phases} sens={sens} reduit={reduit}>
                <div className="mt-4">
                    <p className="text-[15px] leading-7 text-gray-600 dark:text-lightBlue mb-0">
                        {motif === "avant" && remplir(t.motifAvant, annee)}
                        {motif === "pasEncore" && t.motifPasEncore}
                        {motif === "localDedie" && t.motifLocalDedie}
                    </p>
                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            </Resultat>
        );
    }

    if (etape === "entreprise") {
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={1}
                onRetour={retour}
                titre={t.entrepriseTitre}
                aide={t.entrepriseAide}
            >
                <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Champ libelle={t.champPrenoms} valeur={r.prenoms} poser={poser("prenoms")} />
                        <Champ libelle={t.champNom} valeur={r.nom} poser={poser("nom")} />
                    </div>
                    <Champ
                        libelle={t.champSiret}
                        aide={t.aideSiret}
                        valeur={r.siret}
                        poser={poser("siret")}
                        erreur={erreurDe("siret")}
                        inputMode="numeric"
                    />
                    <Champ
                        libelle={t.champCodeApe}
                        aide={t.aideCodeApe}
                        valeur={r.codeApe}
                        poser={poser("codeApe")}
                        erreur={erreurDe("codeApe")}
                    />
                    <Champ
                        libelle={t.champActivites}
                        aide={t.aideActivites}
                        valeur={r.activites}
                        poser={poser("activites")}
                    />
                    <div>
                        <Segments
                            libelle={t.champArtisan}
                            options={[
                                { cle: "oui", libelle: t.oui },
                                { cle: "non", libelle: t.non },
                            ]}
                            valeur={r.artisan}
                            poser={poser("artisan")}
                        />
                        <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-2 mb-0">
                            {t.aideArtisan}
                        </p>
                    </div>
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !rempli("prenoms", "nom", "siret", "activites", "artisan") ||
                        Boolean(refusDe("siret")) ||
                        Boolean(refusDe("codeApe"))
                    }
                    onClick={() => aller("adresse")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "adresse") {
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={1}
                onRetour={retour}
                titre={t.adresseTitre}
                aide={t.adresseAide}
            >
                <div className="space-y-5">
                    <Champ libelle={t.champVoie} valeur={r.voie} poser={poser("voie")} />
                    <div className="grid sm:grid-cols-3 gap-4">
                        <Champ
                            libelle={t.champCodePostal}
                            valeur={r.codePostal}
                            poser={poserCodePostal}
                            erreur={erreurDe("codePostal")}
                            inputMode="numeric"
                        />
                        <div className="sm:col-span-2">
                            <Champ libelle={t.champCommune} valeur={r.commune} poser={poser("commune")} />
                        </div>
                    </div>
                    <Champ
                        libelle={t.champDepartement}
                        aide={t.aideDepartement}
                        valeur={r.departement}
                        poser={poserDepartement}
                    />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !rempli("voie", "codePostal", "commune", "departement") ||
                        Boolean(refusDe("codePostal"))
                    }
                    onClick={() => aller("activite")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "activite") {
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={1}
                onRetour={retour}
                titre={remplir(t.activiteTitre, annee)}
                aide={t.activiteAide}
            >
                <div className="space-y-5">
                    <Champ
                        type="date"
                        libelle={t.champDateCreation}
                        valeur={r.dateCreation}
                        poser={poser("dateCreation")}
                        erreur={erreurDe("dateCreation")}
                    />
                    <Champ
                        libelle={remplir(t.champChiffreDAffaires, annee)}
                        aide={t.aideChiffreDAffaires}
                        valeur={r.chiffreDAffaires}
                        poser={poser("chiffreDAffaires")}
                        erreur={erreurDe("chiffreDAffaires")}
                        inputMode="numeric"
                    />
                    <Champ
                        libelle={t.champSurface}
                        aide={t.aideSurface}
                        valeur={r.surface}
                        poser={poser("surface")}
                        erreur={erreurDe("surface")}
                        inputMode="numeric"
                    />
                    <div className="space-y-4 pt-1">
                        <Case
                            libelle={t.microSocial}
                            aide={t.microSocialAide}
                            coche={r.microSocial}
                            basculer={poser("microSocial")}
                        />
                        {r.microSocial && (
                            <Champ
                                type="date"
                                libelle={t.champDateMicroSocial}
                                valeur={r.dateMicroSocial}
                                poser={poser("dateMicroSocial")}
                                erreur={erreurDe("dateMicroSocial")}
                            />
                        )}
                        <Case
                            libelle={t.tempsPartiel}
                            coche={r.tempsPartiel}
                            basculer={poser("tempsPartiel")}
                        />
                    </div>
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !rempli("dateCreation", "chiffreDAffaires", "surface") ||
                        (r.microSocial && !rempli("dateMicroSocial")) ||
                        Boolean(refusDe("dateCreation")) ||
                        Boolean(refusDe("chiffreDAffaires")) ||
                        Boolean(refusDe("surface"))
                    }
                    onClick={() => aller("signature")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "signature") {
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={2}
                onRetour={retour}
                titre={t.signatureTitre}
                aide={t.signatureAide}
            >
                <div className="space-y-5">
                    <Champ
                        libelle={t.champLieuSignature}
                        valeur={r.lieuDeSignature}
                        poser={poser("lieuDeSignature")}
                    />
                    <Champ
                        type="date"
                        libelle={t.champDateSignature}
                        valeur={r.dateDeSignature || jour}
                        poser={poser("dateDeSignature")}
                        erreur={erreurDe("dateDeSignature")}
                    />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!rempli("lieuDeSignature")}
                    onClick={() => aller("resultat")}
                >
                    {t.voirDocument}
                </Avancer>
            </Ecran>
        );
    }

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            {!recap ? (
                <div className="mt-4">
                    <div className="font-bold text-lg dark:text-white leading-snug">
                        {controle.manques.length ? t.manquesTitre : t.verifierTitre}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {controle.manques.length ? t.manquesAide : t.verifierAide}
                    </p>
                    <ul className="mt-4 space-y-2">
                        {controle.manques.map((cle) => (
                            <li
                                key={cle}
                                className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                            >
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-red-500/70" />
                                <span>{LIBELLES_MANQUES[lang]?.[cle] || LIBELLES_MANQUES.fr[cle]}</span>
                            </li>
                        ))}
                        {(controle.invalides || []).map(({ champ, motif: m }) => (
                            <li
                                key={`${champ}-${m}`}
                                className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                            >
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500/70" />
                                <span>
                                    {LIBELLES_MANQUES[lang]?.[champ] || LIBELLES_MANQUES.fr[champ]}
                                    {" : "}
                                    {erreurDe(champ)}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <Avancer pleineLargeur onClick={retour}>
                        {t.retour}
                    </Avancer>
                </div>
            ) : (
                <div className="mt-4">
                    {controle.alertes.includes("horsDelai") && (
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mb-5">
                            <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">
                                {remplir(t.alerteHorsDelai, { annee: MILLESIME.creationsDe })}
                            </p>
                        </div>
                    )}

                    <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                        {t.recapTitre}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-lightBlue/70 mb-4">{t.recapAide}</p>

                    <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 overflow-hidden">
                        <table className="w-full text-sm">
                            <tbody>
                                {recap.map(([titre, valeur]) => (
                                    <tr
                                        key={titre}
                                        className="border-b last:border-b-0 border-gray-200/70 dark:border-white/10"
                                    >
                                        <td className="px-5 py-3 text-gray-500 dark:text-lightBlue/70 align-top w-2/5">
                                            {titre}
                                        </td>
                                        <td className="px-5 py-3 font-medium dark:text-white">
                                            {valeur}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <button
                        type="button"
                        onClick={telecharger}
                        className="w-full mt-5 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#D04515] text-white font-semibold transition-opacity duration-200 hover:opacity-90"
                    >
                        <SolarIcon name="download-minimalistic-linear" className="text-lg" />
                        {t.telecharger}
                    </button>
                    {erreurTelechargement && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 mb-0">
                            {t.erreurFormat}
                        </p>
                    )}
                    <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-3 mb-0">
                        {remplir(t.millesimeNote, {
                            cerfa: MILLESIME.cerfa,
                            annee: MILLESIME.annee,
                        })}
                    </p>

                    <div className="mt-8">
                        <div className="flex items-center gap-2.5 mb-4">
                            <SolarIcon
                                name="clipboard-check-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-bold dark:text-white leading-snug">
                                {t.suiteTitre}
                            </div>
                        </div>
                        <ul className="space-y-3">
                            {[
                                remplir(t.suite1, { annee: MILLESIME.creationsDe }),
                                t.suite2,
                                t.suite3,
                            ].map((ligne) => (
                                <li
                                    key={ligne}
                                    className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                                >
                                    <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300 dark:bg-white/20" />
                                    <span>{ligne}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="mt-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                        <div className="flex items-center gap-2.5 mb-3">
                            <SolarIcon
                                name="shield-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-semibold dark:text-white leading-snug">
                                {t.fondTitre}
                            </div>
                        </div>
                        <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">
                            {remplir(t.fond, { cfeDe: MILLESIME.cfeDe })}
                        </p>
                    </div>

                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            )}
        </Resultat>
    );
}
